<template>
	<!-- The banner and menu options remain fixed to the top -->
	<nav class="d-flex flex-column nav-top-fixed">
		<HeaderBanner
			prefixImage="/images/home/v6/stan-challenge.png"
			mobilePrefixImage="/images/home/v6/stan-challenge-mobile.png"
			bannerKey="sb-challenge"
			heading="Join the Challenge and get 30 days of business coaching from Steven Bartlett. "
			mobileHeading="Enter to win $100k. Get 30 days of coaching from Steven Bartlett. "
			linkText="Join Challenge"
			mobileLinkText="Join Now"
			primaryColor="#000"
			secondaryColor="#fff"
			:showCloseIcon="false"
			:endDate="endOfSBChallenge"
			@toggle="bannerToggled"
			hideOnEndDate
		/>
		<div class="container-fluid" :class="navbarClass" :style="{ backgroundColor: navWrapperBackground }">
			<div class="navbar-brand">
				<router-link class="router-link--logo" to="/" aria-label="Stan Logo">
					<img src="@/assets/images/stan_logo_v6.svg" alt="" class="d-inline-block align-top stan-logo" />
				</router-link>
				<router-link class="router-link router-link--home" to="/" aria-label="Home" :class="{ unselected: currentPage !== 'Home' }">
					Home
				</router-link>
				<router-link
					class="router-link router-link--our-mission"
					to="/about"
					aria-label="Our Mission"
					:class="{ unselected: currentPage !== 'About' }"
				>
					Our Mission
				</router-link>

				<h3 class="navbtn desktop" @click="scrollToPricing">Pricing</h3>
				<a class="navbtn desktop" href="https://stan.store/blog">Blog</a>
			</div>
			<div class="nav-wrapper">
				<Transition name="fade-slide" mode="out-in">
					<div class="collapse navbar-collapse" v-if="showNav" id="navbarNavAltMarkup" :style="{ backgroundColor: navWrapperBackground }">
						<div class="navbar-menu-nav">
							<router-link
								class="nav-link-menu-text nav-link-menu-text--home"
								to="/"
								aria-label="Home"
								:class="{ unselected: currentPage !== 'Home' }"
								>Home
							</router-link>
							<router-link
								class="nav-link-menu-text nav-link-menu-text--mission"
								to="/about"
								aria-label="Our Mission"
								:class="{ unselected: currentPage !== 'About' }"
								>Our Mission
							</router-link>
							<h3 class="nav-link-menu-text unselected pricing" @click="scrollToPricing">Pricing</h3>
							<a class="nav-link-menu-text unselected pricing" href="https://stan.store/blog">Blog</a>
							<a
								class="nav-link-menu-text nav-link-menu-text--mission unselected"
								@click.prevent="signIn('top-navigation')"
								aria-label="Home"
							>
								Log in
							</a>
							<a
								class="nav-link-menu-text nav-link-menu-text--mission unselected"
								@click.prevent="registerByEvent('top-navigation')"
								aria-label="Home"
							>
								Sign Up
							</a>
						</div>
					</div>
				</Transition>

				<button
					@click="toggleNav"
					@blur="hideNav"
					class="navbar-toggler nav-button p-0"
					type="button"
					aria-label="Toggle Nav"
					:class="{ open: showNav }"
				>
					<span class="navbar-toggler-icon"></span>
				</button>
				<div class="navbar-nav">
					<a class="nav-link nav-link-text log-in" href="#" @click.prevent="signIn('top-navigation')">Log in</a>
					<a class="nav-link nav-link-text sign-up" href="#" @click.prevent="registerByEvent('top-navigation')">Sign Up</a>
				</div>
			</div>
		</div>
	</nav>
</template>

<script setup>
	import { computed, getCurrentInstance, ref, defineProps, onUnmounted, watch, defineEmits } from 'vue'
	import HeaderBanner from '@/components/shared/HeaderBanner'
	import { client } from '@/plugins/platform'
	import { getIsMobile } from '@/plugins/is'

	const props = defineProps({
		page: {
			type: String,
		},
	})
	const emit = defineEmits('toggleNav', 'scrollTo')
	const showNav = ref(false)
	const isMobile = ref(getIsMobile())

	const {
		proxy: { $emitter },
	} = getCurrentInstance()

	const scrollToPricing = () => {
		emit('scrollTo', 'pricing')
		showNav.value = !showNav.value
	}
	// Countdown expiry: JUNE 2 2025, 11PM UTC
	const endOfSBChallenge = new Date(Date.UTC(2025, 5, 2, 23, 0, 0))
	const currentPage = computed(() => props.page)
	const navbarClass = computed(() => {
		const base = 'navbar navbar-expand-md navbar-dark creator-store-nav'
		if (!showNav.value) {
			return base
		}
		return currentPage.value === 'About' ? `${base}` : base
	})

	const navWrapperBackground = computed(() =>
		currentPage.value === 'About' ? 'var(--stan-about-help-anyone-background)' : 'var(--stan-primary-primary-color)'
	)
	const bannerToggled = visible => {
		if (visible && isMobile.value) {
			// Only need to add spacing for mobile, default design has enough head room for banner
			document.documentElement.style.setProperty('--nav-margin', '126px')
		} else {
			document.documentElement.style.setProperty('--nav-margin', '60px')
		}
	}

	const registerByEvent = msg => {
		$emitter.emit('register', msg)
	}

	const signIn = msg => {
		$emitter.emit('sign.in', msg)
	}

	const toggleNav = () => {
		showNav.value = !showNav.value
		emit('toggleNav', showNav.value)
	}

	const hideNav = () => {
		setTimeout(() => (showNav.value = false), 100)
	}

	onUnmounted(() => {
		document.documentElement.style.overflow = 'auto'
	})
</script>

<style lang="scss">
	@import '@/assets/styles/helpers-v6.scss';

	.nav-top-fixed {
		z-index: 10;
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
	}

	.navbar {
		padding: 10px 16px 10px 20px;
	}

	.creator-store-nav {
		padding: 10px 16px 10px 20px;
		width: calc(100vw - 36px);
		justify-content: space-between;
		@media screen and (max-width: 767px) {
			& {
				height: 64px;
				width: calc(100vw - 40px);
				padding-inline: 20px;
				padding-block: 0;
				justify-content: space-between;
				align-items: center;
				background: #747195;
				transform: translateX(0px);
				&.full-height {
					height: 100vh;
					width: 120vw;
					left: 5vw;
					transform: translateX(4vw) !important;
				}
			}

			.desktop {
				display: none;
			}
		}
		&.full-height {
			height: 100vh;
			width: 100vw;
		}

		&.navbar.full-height {
			display: block;
			.nav-wrapper {
				padding-right: 31px;
			}
		}

		.navbtn {
			transition: all 0.3s ease;
			font-family: 'Plus Jakarta Sans', sans-serif;
			font-style: normal;
			font-weight: 600;
			font-size: 16px;
			line-height: 150%;
			letter-spacing: 0.01em;
			padding: 0;
			margin: 0;
			color: #ffffff;
			order: 3;
			opacity: 0.5;
			cursor: pointer;
			&:hover {
				opacity: 1;
			}
		}

		.navbar-brand {
			display: flex;
			width: 318px;
			flex-direction: row;
			align-items: center;
			gap: 40px;
			padding: 0 0;
			font-size: 16px;

			.router-link {
				&--home,
				&--our-mission {
					transition: all 0.3s ease;
					font-family: 'Plus Jakarta Sans', sans-serif;
					font-style: normal;
					font-weight: 600;
					font-size: 16px;
					line-height: 150%;
					letter-spacing: 0.01em;
					color: #ffffff;
					margin: 0;
				}

				&--logo {
					flex: none;
					order: 1;
					flex-grow: 0;
					margin-right: 12px;
				}

				&--home {
					flex: none;
					order: 2;
					flex-grow: 0;
				}

				&--our-mission {
					flex: none;
					order: 3;
					flex-grow: 0;
				}
				&.unselected {
					opacity: 0.5;
					&:hover {
						opacity: 1;
					}
				}
			}
		}
		.navbar-nav {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: flex-end;
			gap: 12px;
			.nav-link-text {
				font-family: 'Plus Jakarta Sans', sans-serif;
				font-style: normal;
				font-weight: 700;
				font-size: 16px;
				line-height: 150%;
				letter-spacing: 0.01em;
				color: #ffffff;
				&.log-in {
					padding-left: 0 !important;
					transition: all 0.3s ease;
					& :hover {
						transform: scale(1.1);
					}
				}
				&.sign-up {
					background: #ffffff;
					border-radius: 10px;
					color: #00106d;
					padding: 8px 16px 10px;
					transition: all 0.3s ease;
					& :hover {
						transform: scale(1.1);
						filter: invert(1);
					}
				}
			}
			@media screen and (max-width: 767px) {
				display: none;
			}
		}
		.nav-wrapper {
			.navbar-collapse {
				.navbar-menu-nav {
					display: none;
					flex-direction: column;
					align-items: end;
					justify-content: space-between;
					width: 75%;

					.navbtn {
						order: 3;
					}

					.nav-link-menu-text {
						& {
							font-family: 'Plus Jakarta Sans', sans-serif;
							font-style: normal;
							font-weight: 800;
							font-size: 44px;
							line-height: 95%;
							text-align: right;
							letter-spacing: -0.01em;
							color: rgba(255, 255, 255, 0.9);
							margin-bottom: 5%;
						}

						&.unselected {
							opacity: 0.5;
							&:hover {
								opacity: 1;
							}
						}
					}
				}
			}
		}

		.navbar-toggler {
			color: transparent !important;
			border-color: transparent !important;
			.navbar-toggler-icon {
				position: relative;
				width: 24px;
				height: 24px;
				display: block;
				background-image: none;
				&::before,
				&::after {
					content: ' ';
					position: absolute;
					top: 0;
					left: 0;
					width: 3px;
					height: 100%;
					background: #fff;
					transform: rotate(90deg) translate(-6px, -11px);
					transition: transform 0.3s ease-in-out;
					-webkit-transition: transform 0.3s ease-in-out;
					-moz-transition: transform 0.3s ease-in-out;
					-ms-transition: transform 0.3s ease-in-out;
					-o-transition: transform 0.3s ease-in-out;
				}
				&::after {
					top: 10px;
				}
			}

			&.open .navbar-toggler-icon {
				&::before {
					transform: rotate(135deg) translate(-10px, -10px);
				}
				&::after {
					transform: rotate(45deg) translate(3px, -17px);
				}
			}
		}

		@media (max-width: $primary-mobile-breakpoint-px) {
			.navbar-brand {
				width: 90px;
				padding-left: 0;
				z-index: 2;
				.router-link {
					&--home,
					&--our-mission {
						display: none;
					}
				}
			}

			.nav-wrapper {
				.collapse {
					position: fixed;
					top: 0;
					bottom: 0;
					right: 0;
					left: 0;
					width: 100vw;
					height: 100vh;
					display: flex;
					align-items: center;
					justify-content: center;
					.navbar-nav {
						display: none;
					}
					.navbar-menu-nav {
						display: flex;
					}
				}
			}
		}
	}

	.fade-slide-enter-from {
		opacity: 0;
		transform: translateX(100%);
	}

	.fade-slide-enter-to {
		opacity: 1;
		transform: translateX(0);
	}

	.fade-slide-enter-active {
		transition: opacity 0.5s ease, transform 0.5s ease;
	}

	.fade-slide-leave-from {
		opacity: 1;
		transform: translateX(0);
	}

	.fade-slide-leave-to {
		opacity: 0;
		transform: translateX(100%);
	}

	.fade-slide-leave-active {
		transition: opacity 0.5s ease, transform 0.5s ease;
	}
</style>
